import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { useToast } from '../hooks/use-toast';

const MIN_DEPOSIT = 300;
const MAX_DEPOSIT = 500000;

const DepositPage = () => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();

  const quickAdd = (value) => {
    const current = parseFloat(amount) || 0;
    setAmount((current + value).toString());
  };

  const handleSubmit = async () => {
    const num = parseFloat(amount);
    if (!amount || isNaN(num)) {
      setError('Please enter an amount.');
      return;
    }
    if (num < MIN_DEPOSIT) {
      setError(`Minimum deposit amount is ₹${MIN_DEPOSIT}.`);
      return;
    }
    if (num > MAX_DEPOSIT) {
      setError(`Maximum deposit amount is ₹${MAX_DEPOSIT.toLocaleString()}.`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      let user = null;
      try {
        const u = localStorage.getItem('reddyannabook_user');
        if (u) user = JSON.parse(u);
      } catch (_) {}
      const token = localStorage.getItem('reddyannabook_token');

      if (!token) {
        if (user && user.id === 'demo') {
          setError('Deposits are not available in Demo mode.');
          toast({ title: 'Demo Mode', description: 'Deposits are disabled for demo accounts.', variant: 'destructive' });
          return;
        }
        navigate('/login');
        return;
      }

      const response = await fetch(API_ENDPOINTS.WALLET_PAYMENT_OPTIONS, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      let data;
      try { data = await response.json(); } catch (_) {
        if (response.status === 401) { setError('Your session has expired. Please log in again.'); return; }
        setError('Invalid response from server. Please try again.'); return;
      }
      if (response.status === 401) { setError('Your session has expired. Please log in again.'); return; }
      if (response.status === 429) { setError('Too many requests. Please wait a few minutes and try again.'); return; }
      if (!response.ok) { setError(data.message || 'Failed to fetch payment options'); return; }

      if (data.success && data.data?.payment_options?.length > 0) {
        navigate('/wallet-deposit', { state: { amount: num } });
      } else {
        setError('No payment options available. Please contact support.');
      }
    } catch (err) {
      setError(err.message || 'Failed to proceed with deposit');
      toast({ title: "Error", description: err.message || 'Failed to proceed with deposit', variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
          <h1 className="text-lg font-bold text-gray-900">Deposit Amount</h1>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-gray-400">₹</span>
            <input
              type="number"
              placeholder="Enter amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full pl-10 pr-4 py-4 text-xl font-bold border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none transition-all"
            />
          </div>

          {/* Min / Max hint */}
          <div className="flex items-center justify-between text-xs font-semibold text-gray-500 -mt-2">
            <span>Min: ₹{MIN_DEPOSIT.toLocaleString('en-IN')}</span>
            <span>Max: ₹{MAX_DEPOSIT.toLocaleString('en-IN')}</span>
          </div>

          <div className="grid grid-cols-4 gap-2">
            {[500, 1000, 2000, 5000, 10000, 25000, 50000, 100000].map(val => (
              <button
                key={val}
                onClick={() => quickAdd(val)}
                className="py-3 rounded-xl text-sm font-bold bg-green-50 text-green-600 border border-green-200 hover:bg-green-500 hover:text-white hover:border-green-500 active:scale-95 transition-all"
              >
                +{val >= 100000 ? (val/100000)+'L' : val >= 1000 ? (val/1000)+'K' : val}
              </button>
            ))}
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setAmount('')}
              className="flex-1 py-3.5 rounded-xl font-bold border-2 border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
            >
              Clear
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-[2] py-3.5 rounded-xl font-bold bg-green-500 hover:bg-green-600 text-white shadow-md shadow-green-200 active:scale-95 transition-all disabled:opacity-60"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Processing...
                </span>
              ) : 'Submit Deposit →'}
            </button>
          </div>
        </div>

        {/* Important Rules */}
        <div className="mt-4 bg-red-50 border border-red-200 rounded-2xl p-4">
          <h4 className="font-bold text-red-700 mb-3 flex items-center gap-2">
            <span>⚠️</span> Important Instructions
          </h4>
          <ol className="space-y-2">
            {[
              'Deposit money only in the below available accounts to get the fastest credits and avoid possible delays.',
              'Deposits made 45 minutes after the account removal from the site are valid & will be added to their wallets.',
              'Site is not responsible for money deposited to Old, Inactive or Closed accounts.',
              'After deposit, add your UTR and amount to receive balance.',
              'NEFT receiving time varies from 40 minutes to 2 hours.',
              'In case of account modification: payment valid for 1 hour after changing account details in deposit page.',
            ].map((rule, i) => (
              <li key={i} className="flex gap-2 text-sm text-red-600">
                <span className="font-bold text-red-700 shrink-0">{i + 1}.</span>
                <span>{rule}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default DepositPage;
